import { Link, Navigate, useParams } from 'react-router-dom';
import Icon from '../components/Icons';
import Revelar from '../components/Revelar';
import Acordeao from '../components/Acordeao';
import NaoEncontrada from './NaoEncontrada';
import { servicoPorSlug, relacionadosDe } from '../data/servicos';
import { destinoDe } from '../data/redirecionamentos';
import { SLUG_BRASILIA } from '../seo';
import { site, praca, linkWhatsApp } from '../config';
import './ServicoPagina.css';

export default function ServicoPagina() {
  const { slug } = useParams();
  const servico = servicoPorSlug(slug);

  // Rede de segurança: o 301 correto é feito pelo servidor, mas se a
  // configuração de hospedagem não estiver aplicada, a URL antiga ainda
  // chega ao destino certo em vez de mostrar um 404.
  const novoDestino = servico ? null : destinoDe(slug);
  if (novoDestino) return <Navigate to={novoDestino} replace />;

  // Slug inexistente: mostra o 404 na própria URL, sem redirecionar.
  if (!servico) return <NaoEncontrada />;

  const pai = servico.pai ? servicoPorSlug(servico.pai) : null;
  const relacionados = relacionadosDe(servico);
  const mensagem = `Olá, gostaria de falar sobre um caso de ${servico.titulo}.`;

  return (
    <article className="servico">
      {/* ---------- topo ---------- */}
      <header className="servico-topo">
        <div className="container">
          <nav className="trilha" aria-label="Você está em">
            <ol>
              <li>
                <Link to="/">Início</Link>
              </li>
              {pai && (
                <li>
                  <Link to={`/${pai.slug}`}>{pai.titulo}</Link>
                </li>
              )}
              <li>
                <span aria-current="page">{servico.titulo}</span>
              </li>
            </ol>
          </nav>

          <div className="servico-topo__grade">
            <div>
              <span className="rotulo">Defesa criminal em todo o Brasil</span>
              <h1 className="servico-topo__titulo">{servico.h1}</h1>

              {servico.intro.map((paragrafo) => (
                <p key={paragrafo.slice(0, 40)} className="servico-topo__texto">
                  {paragrafo}
                </p>
              ))}

              <div className="servico-topo__acoes">
                <a
                  className="btn btn--ouro"
                  href={linkWhatsApp(mensagem)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="whatsapp" size={18} />
                  Falar com o advogado
                </a>
                <a className="btn btn--contorno" href={`tel:${site.telefoneLink}`}>
                  <Icon name="telefone" size={18} />
                  {site.telefone}
                </a>
              </div>
            </div>

            <aside className="servico-topo__cartao">
              <span className="servico-topo__icone" aria-hidden="true">
                <Icon name={servico.icone} size={28} />
              </span>
              <h2>O que abrange</h2>
              <ul>
                {servico.itens.map((item) => (
                  <li key={item}>
                    <Icon name="check" size={16} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="servico-topo__legal">{servico.baseLegal}</p>
            </aside>
          </div>
        </div>
      </header>

      {/* ---------- quando procurar / como atuamos ---------- */}
      <section className="secao secao--escura">
        <div className="container servico-colunas">
          <Revelar>
            <span className="rotulo">Quando procurar</span>
            <h2 className="titulo-secao">Sinais de que é hora de chamar um advogado</h2>
            <ul className="lista-marcada">
              {servico.quandoProcurar.map((item) => (
                <li key={item}>
                  <Icon name="check" size={17} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Revelar>

          <Revelar atraso={120}>
            <span className="rotulo">Como a defesa atua</span>
            <h2 className="titulo-secao">Providências concretas</h2>
            <ul className="lista-marcada lista-marcada--numerada">
              {servico.comoAtuamos.map((item, indice) => (
                <li key={item}>
                  <span className="lista-marcada__numero">
                    {String(indice + 1).padStart(2, '0')}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Revelar>
        </div>
      </section>

      {/* ---------- atendimento nacional + recorte local ---------- */}
      <section className="secao">
        <div className="container servico-alcance">
          <Revelar className="servico-alcance__nacional">
            <span className="rotulo">Atendimento nacional</span>
            <h2 className="titulo-secao">Onde quer que o caso esteja</h2>
            <p>
              A atuação alcança clientes de todos os estados. A orientação
              inicial, a análise dos documentos e as reuniões são feitas por
              videoconferência; o deslocamento presencial acontece quando o ato
              exige — audiência, sustentação oral, diligência ou atendimento em
              delegacia.
            </p>
            <p>
              Antes de qualquer contratação fica definido, por escrito, o que
              será feito à distância e o que exigirá presença física. Não há
              promessa de comparecimento em qualquer comarca do país sem essa
              confirmação prévia.
            </p>
          </Revelar>

          <Revelar className="servico-alcance__local" atraso={120}>
            <span className="servico-alcance__selo">
              <Icon name="local" size={18} />
              {praca}
            </span>
            <h2>Aqui, presencialmente</h2>
            <p>{servico.brasilia}</p>
            <Link className="servico-alcance__link" to={`/${SLUG_BRASILIA}`}>
              Atuação criminal em {site.endereco.cidade}
              <Icon name="seta" size={17} />
            </Link>
          </Revelar>
        </div>
      </section>

      {/* ---------- perguntas ---------- */}
      <section className="secao secao--escura">
        <div className="container servico-faq">
          <div className="cabecalho-secao cabecalho-secao--centro">
            <span className="rotulo">Dúvidas Frequentes</span>
            <h2 className="titulo-secao">
              Perguntas sobre {servico.titulo.toLowerCase()}
            </h2>
          </div>
          <Acordeao itens={servico.faq} idBase={servico.id} />
        </div>
      </section>

      {/* ---------- chamada final ---------- */}
      <section className="servico-chamada">
        <div className="container servico-chamada__interno">
          <div>
            <h2>Precisa de defesa em {servico.titulo.toLowerCase()}?</h2>
            <p>
              O primeiro contato é sigiloso e não gera compromisso. Casos
              urgentes são atendidos fora do horário comercial.
            </p>
          </div>
          <a
            className="btn btn--claro"
            href={linkWhatsApp(mensagem)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="whatsapp" size={18} />
            Falar agora
          </a>
        </div>
      </section>

      {/* ---------- próximos passos do mesmo tema ---------- */}
      <section className="secao">
        <div className="container">
          <div className="cabecalho-secao">
            <span className="rotulo">Continue por aqui</span>
            <h2 className="titulo-secao">
              {pai ? `Mais sobre ${pai.titulo.toLowerCase()}` : 'Temas ligados a este'}
            </h2>
          </div>

          <ul className="servico-relacionados">
            {relacionados.map((item) => (
              <li key={item.id}>
                <Link to={`/${item.slug}`}>
                  <Icon name={item.icone} size={22} />
                  <span>
                    <strong>{item.h1}</strong>
                    <span>{item.resumo}</span>
                  </span>
                  <Icon name="seta" size={18} className="servico-relacionados__seta" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </article>
  );
}
